import React from 'react';
import PropTypes from 'prop-types';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useProfile } from '../../contexts/ProfileContext';
import { useBootstrapComponents } from '../../hooks/useBootstrapComponents';
import '../../assets/styles/header.css';

function Header({ toggleSidebar }) {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const { hasActiveProfile } = useProfile();
  const navigate = useNavigate();

  // Initialize Bootstrap components scoped to the header only
  useBootstrapComponents(
    [user, hasActiveProfile],
    'header [data-bs-toggle="tooltip"]',
    'header [data-bs-toggle="dropdown"]'
  );

  const handleProfileSwitchClick = () => {
    navigate('/switch-profile');
  };

  const changeLanguage = lng => {
    i18n.changeLanguage(lng);
  };

  const currentLanguage = i18n.language || 'nl';

  const getFlagIcon = lang => {
    const flags = {
      en: `${window._env_.REACT_APP_CDN_BASE_URL}/img/flag-gb-16x16.png`,
      nl: `${window._env_.REACT_APP_CDN_BASE_URL}/img/flag-nl-16x16.png`,
      gb: `${window._env_.REACT_APP_CDN_BASE_URL}/img/flag-gb-16x16.png`,
    };
    return (
      flags[lang.toLowerCase()] || `${window._env_.REACT_APP_CDN_BASE_URL}/img/flag-nl-16x16.png`
    );
  };

  const getLanguageLabel = lang => {
    const labels = {
      en: 'English',
      nl: 'Nederlands',
      gb: 'English',
    };
    return labels[lang.toLowerCase()] || 'Nederlands';
  };

  return (
    <header className="navbar navbar-expand-lg navbar-light bg-white shadow-sm border-bottom">
      <div className="container-fluid px-3 d-flex justify-content-between align-items-center">
        {/* Left side content */}
        <div className="d-flex align-items-center">
          {/* Sidebar toggle */}
          <button
            className="btn btn-link text-dark me-2 p-1"
            onClick={toggleSidebar}
            title="Toggle Sidebar"
          >
            <i className="bi bi-list fs-4"></i>
          </button>

          {/* Logo */}
          <Link className="navbar-brand d-flex align-items-center m-0" to="/">
            <img
              src={`${window._env_.REACT_APP_CDN_BASE_URL}/img/logo-gearsoft-small.png`}
              alt="GearSoft"
              height="32"
              className="me-2"
              onError={e => {
                // Fallback to text if image fails to load
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'inline';
              }}
            />
            <span className="fw-bold text-primary d-none">{t('app.title')}</span>
          </Link>
        </div>

        {/* Right side content */}
        <div className="d-flex align-items-center gap-3">
          {/* Language Switcher */}
          <div className="user-language-section">
            <div className="dropdown language-dropdown">
              <button
                className="dropdown-toggle"
                type="button"
                id="languageDropdown"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <img
                  src={getFlagIcon(currentLanguage)}
                  alt={getLanguageLabel(currentLanguage)}
                  width="16"
                  height="16"
                  className="flag-icon"
                  style={{ objectFit: 'cover' }}
                />
                <span className="language-text d-none d-md-inline ms-2">
                  {getLanguageLabel(currentLanguage)}
                </span>
              </button>
              <ul className="dropdown-menu dropdown-menu-end" aria-labelledby="languageDropdown">
                <li>
                  <button
                    className={`dropdown-item ${currentLanguage === 'en' ? 'active' : ''}`}
                    onClick={() => changeLanguage('en')}
                  >
                    <img
                      src={getFlagIcon('gb')}
                      alt="English"
                      width="16"
                      height="16"
                      className="flag-icon me-2"
                      style={{ objectFit: 'cover' }}
                    />
                    <span>English</span>
                  </button>
                </li>
                <li>
                  <button
                    className={`dropdown-item ${currentLanguage === 'nl' ? 'active' : ''}`}
                    onClick={() => changeLanguage('nl')}
                  >
                    <img
                      src={getFlagIcon('nl')}
                      alt="Nederlands"
                      width="16"
                      height="16"
                      className="flag-icon me-2"
                      style={{ objectFit: 'cover' }}
                    />
                    <span>Nederlands</span>
                  </button>
                </li>
              </ul>
            </div>
          </div>
          {/* User Profile Display */}
          <div className="user-profile-section">
            {/* User Actions Dropdown */}
            <div className="dropdown">
              <a
                href="#"
                className="d-flex align-items-center link-body-emphasis text-decoration-none dropdown-toggle show"
                data-bs-toggle="dropdown"
                aria-expanded="true"
              >
                {/* User Avatar */}
                <div className="user-avatar px-2 py-1">
                  {user?.avatar ? (
                    <img
                      src={user.avatar}
                      alt={user.name || 'User'}
                      className="rounded-circle"
                      width="28"
                      height="28"
                      style={{ objectFit: 'cover' }}
                    />
                  ) : (
                    <div
                      className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center"
                      style={{ width: '28px', height: '28px', fontSize: '12px' }}
                    >
                      {(user?.name || 'U').charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>
                {/* User Name */}
                <span className="fw-medium text-dark user-name d-none d-sm-inline">
                  {user?.name || 'User'}
                </span>
              </a>
              <ul className="dropdown-menu dropdown-menu-end" aria-labelledby="userMenuDropdown">
                <li className="dropdown-header">
                  <small className="text-muted">{user?.email}</small>
                </li>
                <li>
                  <hr className="dropdown-divider" />
                </li>
                <li style={{ display: hasActiveProfile ? 'block' : 'none' }}>
                  <button className="dropdown-item" onClick={handleProfileSwitchClick}>
                    <i className="bi bi-person-gear me-2"></i>
                    {t('auth.switchProfile')}
                  </button>
                </li>
                <li>
                  <button className="dropdown-item" onClick={logout}>
                    <i className="bi bi-box-arrow-right me-2"></i>
                    {t('auth.logout')}
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}

Header.propTypes = {
  toggleSidebar: PropTypes.func.isRequired,
};

export default Header;
